import { action, computed, makeObservable, observable } from 'mobx';
import { LessonMode, ScoreState } from '../../../Constants';
import lessonManager from '../LessonManager';

export class LessonElementState {
  @observable
  mathRenderDirty = false;

  @observable
  readOnly = false;

  @observable
  currentResponseAnswer = null; // current response of the lesson element;

  @observable
  cachedResponseAnswer = null; // the response after it's submitted

  cachedCorrectState= null; // the response after it's submitted

  @observable
  toolPersistence = {}; // Allows for persisting the state of the tool as well as arbitrary data

  @observable
  serverResponse = null; // response structure needed for saving to the backend

  @observable
  isSaved=false;

  @observable
  isSubmitted=false;

  @observable
  isScored=false;

  @observable
  maxScore=0;

  @observable
  scoreState = null;

  @observable
  responseId=null;

  @observable
  subscore = null;

  @observable
  viewedTime = 0;

  @observable
  controllerList = [];

  @observable
  dependentList = [];

  @observable
  isTestItem = false;

  @observable
  markedForReview = false;

  @observable
  submittedItemScore = 0;

  @observable
  submittedMaxScore = 0;

  @observable
  comment = null;

  @observable
  bedrockUsageId = null;

  @observable
  isLocked = false;

  @observable
  hasChangedFromAiScore = null; // null = not checked, false = matches AI, true = changed from AI

  // The object that will implement functionality for each question type
  questionBehavior = null;

  correctAnswerShowing = false;

  turnOnAnswerShowing = () => {
    this.correctAnswerShowing = true;
  }

  @action
  setSubmittedItemScore = (score) => {
    this.submittedItemScore = score;
  }

  @action
  setIsScored = (toggle) => {
    this.isScored = toggle;
  }

  @action
  setIsSaved = (toggle) => {
    this.isSaved = toggle;
  }

  @action
  setScoreState = (state) => {
    this.scoreState = state;
  }

  turnOffAnswerShowing = () => {
    this.correctAnswerShowing = false;
  }

  @action
  setMathRenderDirty = (toggle) => {
    this.mathRenderDirty = toggle;
  }

  @action
  setComment = (comment) => {
    this.comment = comment;
  }

  @action
  setIsLocked = (toggle) => {
    this.isLocked = toggle;
  }

  @action
  setCurrentResponse = (response) => {
    this.currentResponseAnswer = response;
  }

  setCachedCorrectState = (state) => {
    this.cachedCorrectState = state;
  }

  @action
  setCachedResponse = (response, score) => {
    this.cachedResponseAnswer = response;
    this.cachedResponseAnswer.submittedItemScore = score;
  }

  @action
  setFeedback = (feedback, playerMode) => {
    if (feedback && this.currentResponseAnswer) {
      if(this.scoreState !== ScoreState.MANUALLY_SCORED && this.scoreState !== ScoreState.MANUALLY_OVERRIDDEN
      && playerMode !== LessonMode.REVIEW) {
        this.setSubmittedItemScore(feedback.score);
      }
      this.setIsLocked(feedback.locked);
      this.currentResponseAnswer.omittedList = feedback.omittedList;
      this.currentResponseAnswer.selectedIncorrectList = feedback.selectedIncorrectList;
      this.currentResponseAnswer.trueCorrectList = feedback.trueCorrectList;
      this.currentResponseAnswer.answerResults = feedback.answerResults;
    }
  }

  @action
  setBedrockUsageId = (id) => {
    this.bedrockUsageId = id;
  }

  @action
  setHasChangedFromAiScore = (hasChanged) => {
    this.hasChangedFromAiScore = hasChanged;
  }

  @action
  setSubmitted = (s) => {
    this.isSubmitted = s;
  }

  @action
  addController = (id) => {
    if (this.controllerList.indexOf(id) < 0) {
      this.controllerList.push(id);
    }
  }

  @action
  removeController = (id) => {
    if (this.controllerList.indexOf(id) >= 0) {
      this.controllerList.remove(id);
    }
  }

  @action
  addDependent = (id) => {
    if (this.dependentList.indexOf(id) < 0) {
      this.dependentList.push(id);
    }
  }

  @action
  removeAllDependents = () => {
    this.dependentList.clear();
  }

  @action
  setPersistentToolData = (toolId, updatedValueObj) => {
    // Here we create the tool persistence object if it doesn't exist and override old values with the new ones leaving the rest alone
    if (!this.toolPersistence[toolId]) {
      this.toolPersistence[toolId] = {};
    }
    Object.assign(this.toolPersistence[toolId], updatedValueObj);
  };

  @computed
  get isTryAgain() {
    return (!this.isSubmitted && this.readOnly && this.cachedResponseAnswer !== null);
  }

  @computed
  get isDependencyLocked() {
    return this.controllerList.length > 0;
  }

  getPersistentToolData = (toolId) => {
    return this.toolPersistence[toolId];
  };

  getPersistentToolDataValue = (toolId, key) => {
    return this.toolPersistence[toolId]?.[key];
  };

  constructor() {
    makeObservable(this);
  }
}
